'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Mail, MapPin, Globe, Shield, ArrowUpRight, Phone } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export function Footer() {
  const { user } = useAuth();
  return (
    <footer className="w-full bg-[#082B52] text-white mt-auto border-t-4 border-[#D71920]">
      {/* Top Banner: Slogan Solidarity Forever */}
      <div className="border-b border-white/10 bg-[#06203D] py-3.5">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#D71920] animate-pulse" />
            <span className="font-extrabold tracking-wider text-white text-xs sm:text-sm uppercase">
              SOLIDARITY FOREVER
            </span>
            <span className="text-slate-400">— Semboyan Abadi Arek Mesin ITN Malang</span>
          </div>
          <div className="flex items-center gap-2 text-slate-300 font-medium text-[11px] sm:text-xs">
            <Shield className="h-3.5 w-3.5 text-[#2B78D6]" />
            <span>Akreditasi Unggul • Program Studi Teknik Mesin ITN Malang</span>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Organization Profile Column */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white p-1 border border-white/20 shadow-xs overflow-hidden">
                <Image
                  src="/images/logo-ika-mesin.png"
                  alt="Logo IKA MESIN ITN MALANG"
                  width={44}
                  height={44}
                  className="object-contain"
                />
              </div>
              <div>
                <h3 className="font-bold text-lg text-white tracking-tight flex items-center gap-1.5 leading-tight">
                  IKA MESIN
                  <span className="rounded bg-[#D71920] px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-wider">
                    ITN MALANG
                  </span>
                </h3>
                <p className="text-[11px] text-slate-300">
                  Ikatan Keluarga Alumni Teknik Mesin Institut Teknologi Nasional Malang
                </p>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed pr-4">
              Wadah resmi silaturahmi, sinergi profesional, dan kontribusi nyata seluruh alumni Teknik Mesin ITN Malang lintas angkatan di berbagai sektor industri nasional dan internasional.
            </p>

            <div className="space-y-2 text-xs text-slate-300 pt-2">
              <div className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 text-[#D71920] shrink-0 mt-0.5" />
                <span>Sekretariat: Kampus II ITN Malang, Jl. Raya Karanglo KM 2, Malang, Jawa Timur</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 text-[#D71920] shrink-0" />
                <span>Hotline: +62 812-3456-7890</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-[#2B78D6] shrink-0" />
                <a href="mailto:ikamesin@itn.ac.id" className="hover:text-white transition-colors underline-offset-4 hover:underline">
                  ikamesin@itn.ac.id
                </a>
              </div>
            </div>
          </div>

          {/* Column 2: Layanan Alumni */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-white uppercase tracking-wider border-b border-white/10 pb-2">
              Layanan Alumni
            </h4>
            <ul className="space-y-2 text-xs text-slate-300">
              <li>
                <Link href="/alumni" className="hover:text-white transition-colors flex items-center justify-between group">
                  <span>Direktori Alumni</span>
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link href="/tracer-study" className="hover:text-white transition-colors flex items-center justify-between group">
                  <span>Tracer Study Prodi</span>
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link href="/karir" className="hover:text-white transition-colors flex items-center justify-between group">
                  <span>Bursa Karir & Magang</span>
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link href="/kegiatan" className="hover:text-white transition-colors flex items-center justify-between group">
                  <span>Agenda & Event</span>
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              {user && (
                <li>
                  <Link href="/donasi" className="hover:text-white transition-colors flex items-center justify-between group">
                    <span>Donasi & Sinergi</span>
                    <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Column 3: Portal Anggota */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-white uppercase tracking-wider border-b border-white/10 pb-2">
              Akses Anggota
            </h4>
            <ul className="space-y-2 text-xs text-slate-300">
              <li>
                <Link href="/login" className="hover:text-white transition-colors flex items-center justify-between group">
                  <span>Masuk Portal Alumni</span>
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link href="/register" className="hover:text-white transition-colors flex items-center justify-between group">
                  <span>Registrasi & Verifikasi NIM</span>
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link href="/dashboard" className="hover:text-white transition-colors flex items-center justify-between group">
                  <span>Dashboard Anggota</span>
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
              <li>
                <Link href="/admin" className="hover:text-white transition-colors flex items-center justify-between group">
                  <span>Panel Pengurus / Admin</span>
                  <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Tautan Institusi */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-white uppercase tracking-wider border-b border-white/10 pb-2">
              Tautan Terkait
            </h4>
            <ul className="space-y-2 text-xs text-slate-300">
              <li>
                <a
                  href="https://itn.ac.id"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-white transition-colors flex items-center justify-between group"
                >
                  <span>Institut Teknologi Nasional Malang</span>
                  <Globe className="h-3 w-3 text-slate-400 group-hover:text-white" />
                </a>
              </li>
              <li>
                <a
                  href="https://mesin.itn.ac.id"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-white transition-colors flex items-center justify-between group"
                >
                  <span>Program Studi Teknik Mesin S1</span>
                  <Globe className="h-3 w-3 text-slate-400 group-hover:text-white" />
                </a>
              </li>
            </ul>

            <div className="pt-3">
              <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-[11px] text-slate-300 leading-snug">
                Terhubung dalam Kekeluargaan, Bergerak untuk Kemajuan Teknik Mesin ITN Malang.
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Copyright */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-slate-400">
          <div>
            © {new Date().getFullYear()} IKA MESIN ITN MALANG. Hak Cipta Dilindungi.
          </div>
          <div className="flex items-center gap-4">
            <Link href="/" className="hover:text-slate-200 transition-colors">
              Beranda
            </Link>
            <span>•</span>
            <Link href="/alumni" className="hover:text-slate-200 transition-colors">
              Direktori
            </Link>
            <span>•</span>
            <Link href="/login" className="hover:text-slate-200 transition-colors">
              Portal Alumni
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
